import { useEffect, useState } from "react";
import { Loader2, Truck } from "lucide-react";
import { api, formatRp } from "@/lib/api";
import { useStoreInfo } from "@/lib/store";
import { matchDestination } from "@/components/shop/AddressAutocomplete";
import { Select, useList } from "@/components/shop/RegionFields";

const KEY = "ellmom_ship_city";
const load = () => { try { return JSON.parse(localStorage.getItem(KEY)) || { province: "", city: "" }; } catch { return { province: "", city: "" }; } };

const Result = ({ loading, options, dest }) => {
  if (loading) return <p className="flex items-center gap-2 text-stone-500"><Loader2 className="h-4 w-4 animate-spin" /> Menghitung ongkir...</p>;
  if (!dest || !options.length) return <p className="text-red-700" data-testid="shipping-estimate-empty">Belum ada tarif untuk kota ini. Hubungi CS kami.</p>;
  return (
    <ul className="divide-y divide-stone-100" data-testid="shipping-estimate-list">
      {options.map((o) => (
        <li key={o.code} className="flex items-center justify-between gap-3 py-2" data-testid={`shipping-estimate-${o.code}`}>
          <span><span className="font-medium">{o.name}</span> <span className="text-stone-500">{o.service}{o.etd ? ` · ${o.etd}` : ""}</span></span>
          <span className="tnum shrink-0 whitespace-nowrap font-semibold">{o.cost === 0 ? "Gratis" : formatRp(o.cost)}</span>
        </li>
      ))}
    </ul>
  );
};

export const ShippingEstimate = ({ items, subtitle, note, className = "mt-6" }) => {
  const store = useStoreInfo();
  const [loc, setLoc] = useState(load);
  const [options, setOptions] = useState([]);
  const [loading, setLoading] = useState(false);
  const provinces = useList({}, true);
  const cities = useList({ province: loc.province }, !!loc.province);
  const dest = loc.city ? matchDestination(store.destinations || [], loc) : null;
  const payload = JSON.stringify(items.map((i) => ({ product_id: i.product_id, qty: i.qty, variant: i.variant || {} })));

  useEffect(() => { localStorage.setItem(KEY, JSON.stringify(loc)); }, [loc]);
  useEffect(() => {
    if (!dest) { setOptions([]); return; }
    let live = true;
    setLoading(true);
    api.post("/shipping/quote", { destination: dest.destination, province: loc.province, items: JSON.parse(payload) })
      .then((r) => { if (live) setOptions(r.data.options); }).catch(() => { if (live) setOptions([]); })
      .finally(() => { if (live) setLoading(false); });
    return () => { live = false; };
  }, [dest?.destination, loc.province, payload]); // eslint-disable-line react-hooks/exhaustive-deps

  return (
    <div className={`${className} space-y-3 rounded-lg border border-stone-200 bg-white p-4 text-sm`} data-testid="shipping-estimate">
      <div className="flex items-start gap-3">
        <Truck className="mt-0.5 h-5 w-5 shrink-0 text-amber-700" />
        <div><p className="font-semibold">Cek ongkir</p><p className="text-stone-600">Dikirim dari {store.origin_city || "Kosambi, Tangerang"}{subtitle ? ` · ${subtitle}` : ""}</p></div>
      </div>
      <div className="grid gap-3 sm:grid-cols-2">
        <Select id="estimate-province" label="Provinsi tujuan" value={loc.province} options={provinces} placeholder="Pilih provinsi" onChange={(v) => setLoc({ province: v, city: "" })} />
        <Select id="estimate-city" label="Kota / Kabupaten" value={loc.city} options={cities} disabled={!loc.province} placeholder="Pilih kota" onChange={(v) => setLoc((l) => ({ ...l, city: v }))} />
      </div>
      {loc.city && <Result loading={loading || !store.destinations} options={options} dest={dest} />}
      {loc.city && <p className="text-xs text-stone-500">{note}</p>}
    </div>
  );
};
